<script setup>
import { computed, onMounted, provide, ref } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import { ArrowRightIcon, UserGroupIcon } from '@heroicons/vue/24/outline';
import { TabGroup, TabList, Tab, TabPanels } from '@headlessui/vue';
import scrollIntoView from 'scroll-into-view-if-needed';
import Keyvisual from './Partials/Keyvisual.vue';
import GeneralTabPanel from './Partials/GeneralTabPanel.vue';
import ProgramTabPanel from './Partials/ProgramTabPanel.vue';
import RepetitionDates from './Partials/RepetitionDates.vue';
import ContributorsTabPanel from './Partials/ContributorsTabPanel.vue';
import CooperationsTabPanel from './Partials/CooperationsTabPanel.vue';
import CooperationList from './Partials/CooperationList.vue';
import { useTitle as title } from '@/Composables/title.js';
import { useGetLocalizedDateTime } from '@/Composables/localizedDateTime.js';
import Breadcrumb from '@/Components/Breadcrumb.vue';
import Alert from '@/Components/Alert.vue';
import { useTrackEvent } from '@/Composables/gtmTrackEvent.js';
import ReviewTabPanel from '@/Pages/Event/Partials/ReviewTabPanel.vue';
import RelatedContent from '@/Pages/Event/Partials/RelatedContent.vue';
import { useI18n } from '@/Composables/i18n.js';

const props = defineProps({
    programPackage: {
        type: Object,
        required: true,
    },
    relatedContent: {
        type: Object,
        default: () => ({ articles: [], events: [] }),
    },
    schemaOrg: {
        type: String,
        default: null,
    },
});

const selectedTab = ref(0);
const tabs = ref();

const page = usePage();
const locale = computed(() => page.props.locale);
const longLocale = computed(() => page.props.longLocale);
const translations = computed(() => page.props.translations);
const currentUrl = computed(() => page.props.currentUrl);
const appName = computed(() => page.props.appName);
const errors = computed(() => page.props.errors);

const { getTranslationsByPrefix } = useI18n();

const changeTab = index => {
    selectedTab.value = index;

    scrollIntoView(tabs.value, {
        scrollMode: 'if-needed',
        block: 'nearest',
    });
};

const tabIsShowable = name => {
    const map = {
        contributors: props.programPackage.contributions.length,
        cooperations: props.programPackage.cooperations.length,
        program: Object.keys(props.programPackage.program_items).length > 0,
        review: Object.keys(props.programPackage.event_container.review).length > 0,
    };

    return map[name] ?? true;
};

provide('programPackage', props.programPackage);
provide('relatedContent', props.relatedContent);
provide('locale', locale);
provide('translations', translations);

/* eslint-disable camelcase */
onMounted(() => useTrackEvent('view_item', {
    ecommerce: {
        items: [
            {
                item_name: props.programPackage.title,
                item_id: props.programPackage.event_number,
                price: props.programPackage.event_format.price / 100,
                item_category: props.programPackage.event_business_areas?.map(businessArea => businessArea.name).join(','),
                item_category2: props.programPackage.event_topics?.map(eventTopics => eventTopics.name).join(','),
                item_category3: props.programPackage.tags?.join(','),
                quantity: 1,
            },
        ],
    },
}));
/* eslint-enable camelcase */
</script>

<template>
    <Head>
        <title>{{ title(programPackage.meta_title_with_fallback) }}</title>
        <meta
            name="description"
            :content="programPackage.meta_description_with_fallback"
        >
        <meta
            property="og:locale"
            :content="longLocale"
        >
        <meta
            property="og:url"
            :content="currentUrl"
        >
        <meta
            property="og:site_name"
            :content="appName"
        >
        <meta
            property="og:type"
            content="website"
        >
        <meta
            property="og:title"
            :content="programPackage.open_graph_title_with_fallback"
        >
        <meta
            property="og:description"
            :content="programPackage.open_graph_description_with_fallback"
        >
        <meta
            property="og:image"
            :content="programPackage.open_graph_image_url_with_fallback"
        >
        <template v-if="programPackage.extra_metadata">
            {{ programPackage.extra_metadata }}
        </template>
        <template v-if="schemaOrg">
            {{ schemaOrg }}
        </template>
    </Head>

    <div class="relative bg-white pb-12 lg:pb-20">
        <div class="container px-4 md:py-4 mx-auto">
            <Breadcrumb
                class="mt-4"
            />

            <div v-if="errors.general">
                <Alert>{{ errors.general }}</Alert>
            </div>

            <Keyvisual>
                <template
                    #header
                >
                    <span
                        v-if="programPackage.booked_out_at"
                        class="hidden z-[1] md:flex absolute top-0 right-0 text-sm font-semibold uppercase justify-center items-center bg-sunset-orange-500 text-white rounded-xl mb-3.5 px-4 py-3 mx-8 mt-5"
                    >
                        {{ $t('eventDetails.label.bookedOutAt') }}
                    </span>
                    <span
                        v-if="programPackage.is_canceled"
                        class="hidden z-[1] md:flex absolute top-0 right-0 text-sm font-semibold uppercase justify-center items-center bg-sunset-orange-500 text-white rounded-xl mb-3.5 px-4 py-3 mx-8 mt-5"
                    >
                        {{ $t('eventDetails.label.isCanceled') }}
                    </span>
                </template>

                <div>
                    <span
                        v-if="programPackage.booked_out_at"
                        class="flex text-sm font-semibold md:hidden uppercase justify-center items-center bg-sunset-orange-500 text-white rounded-xl mb-3.5 px-4 py-3"
                    >
                        {{ $t('eventDetails.label.bookedOutAt') }}
                    </span>
                    <span
                        v-if="programPackage.is_canceled"
                        class="flex text-sm font-semibold md:hidden uppercase justify-center items-center bg-sunset-orange-500 text-white rounded-xl mb-3.5 px-4 py-3"
                    >
                        {{ $t('eventDetails.label.isCanceled') }}
                    </span>

                    <span class="inline-flex items-center font-semibold text-sunset-orange-500 mb-1.5">
                        <span
                            v-if="programPackage.event_format.event_format_category.icon"
                            class="w-5 h-5 mr-2"
                            aria-hidden="true"
                            v-html="programPackage.event_format.event_format_category.icon"
                        />
                        {{ programPackage.event_format.event_format_category.name }}
                    </span>

                    <h1 class="text-3xl lg:text-4xl font-semibold text-deep-teal-700 leading-[2.25rem] md:pr-20 md:line-clamp-2">
                        {{ programPackage.title }}
                    </h1>

                    <p
                        v-if="programPackage.subtitle"
                        class="text-gray-700 mt-2 lg:mt-0.5 md:pr-32 md:line-clamp-1"
                    >
                        {{ programPackage.subtitle }}
                    </p>
                </div>

                <div class="space-y-8 mt-5 md:mt-0">
                    <div class="pl-4 space-y-3 border-l-2 border-l-gray-300">
                        <div class="flex flex-col space-y-3 text-lg/none font-semibold text-gray-700">
                            <div>
                                <time :datetime="programPackage.start_date">
                                    {{ useGetLocalizedDateTime(programPackage.start_date) }}
                                </time>

                                {{ $t('eventDetails.keyvisual.to') }}
                            </div>
                            <div>
                                <time :datetime="programPackage.end_date">
                                    {{ useGetLocalizedDateTime(programPackage.end_date) }}
                                </time>
                            </div>
                        </div>

                        <div class="text-xs/none font-light text-gray-700">
                            {{ $t('eventDetails.keyvisual.timezone') }}
                        </div>
                    </div>

                    <component
                        :is="programPackage.is_checkoutable ? Link : 'button'"
                        :disabled="!programPackage.is_checkoutable"
                        :href="programPackage.is_checkoutable ? route('event-checkout.show', { eventFormatCategory: programPackage.event_format.event_format_category.slug, programPackage: programPackage.slug }) : null"
                        class="w-full md:w-auto inline-flex items-center justify-center md:justify-start font-bold text-base rounded-3xl px-5 py-2.5 transition-colors"
                        :class="[!programPackage.is_checkoutable ? 'text-gray-400 bg-golden-fizz-200 border-golden-fizz-200 hover:bg-golden-fizz-200 hover:border-golden-fizz-200' : 'text-gray-700 bg-golden-fizz-500 border-golden-fizz-500 hover:bg-golden-fizz-600 hover:border-golden-fizz-600 focus:outline-none focus:bg-golden-fizz-600 focus:border-golden-fizz-600 border-2']"
                    >
                        {{ !programPackage.booked_out_at ? $t('eventDetails.keyvisual.cta') : $t('eventDetails.waitList.cta') }}
                        <ArrowRightIcon
                            class="w-4 h-4 ml-3 [&>path]:stroke-[2]"
                            aria-hidden="true"
                        />
                    </component>
                </div>

                <template
                    v-if="programPackage.glide_keyvisual_image_url_avif"
                    #figure
                >
                    <picture>
                        <source
                            :srcset="programPackage.glide_keyvisual_image_url_avif"
                            type="image/avif"
                        >
                        <source
                            :srcset="programPackage.glide_keyvisual_image_url_webp"
                            type="image/webp"
                        >
                        <img
                            class="object-cover w-full h-64 sm:h-56 md:h-full"
                            :src="programPackage.glide_keyvisual_image_url_jpg"
                            :alt="programPackage.keyvisual_image_alt_with_fallback"
                            fetchpriority="high"
                        >
                    </picture>
                </template>

                <template
                    v-if="programPackage.keyvisual_image_credit_line"
                    #creditLine
                >
                    {{ $t('components.creditLine.expandedPrefix') }} {{ programPackage.keyvisual_image_credit_line }}
                </template>
            </Keyvisual>
        </div>

        <div class="container px-4 md:py-4 mx-auto">
            <RepetitionDates
                v-if="programPackage.event_container.mode === 'repetition' && programPackage.event_container.program_packages"
                :program-packages="programPackage.event_container.program_packages"
            />

            <!-- content sections -->
            <TabGroup
                as="main"
                :selected-index="selectedTab"
                class="mt-8"
                @change="changeTab"
            >
                <div>
                    <label
                        for="current-tab"
                        class="inline-block font-semibold text-gray-700 text-sm lg:sr-only mb-1.5"
                    >
                        {{ $t('eventDetails.label.infoMenu') }}
                    </label>
                </div>

                <div class="flex items-center justify-between border-b border-gray-200 pb-4 lg:pb-0">
                    <div
                        ref="tabs"
                        class="w-full"
                    >
                        <div class="lg:hidden">
                            <select
                                id="current-tab"
                                name="current-tab"
                                class="w-full sm:w-1/2 form-select focus:border-deep-teal-600 focus:ring-1 focus:ring-deep-teal-600 rounded-md"
                                @change="changeTab(Number($event.target.value))"
                            >
                                <option
                                    v-for="(section, key, index) in getTranslationsByPrefix('eventDetails.tabs')"
                                    v-show="tabIsShowable(key)"
                                    :key
                                    :value="index"
                                    :selected="selectedTab === index"
                                >
                                    {{ section }}
                                </option>
                            </select>
                        </div>

                        <TabList
                            as="nav"
                            class="hidden lg:block space-x-8 -mb-px"
                        >
                            <template
                                v-for="(tab, key) in getTranslationsByPrefix('eventDetails.tabs')"
                                :key
                            >
                                <Tab
                                    v-show="tabIsShowable(key)"
                                    v-slot="{ selected }"
                                    as="template"
                                >
                                    <button
                                        :class="[
                                            selected ? 'border-sunset-orange-500 text-sunset-orange-500' : 'border-transparent text-gray-700 hover:text-sunset-orange-500 focus:text-sunset-orange-400',
                                            'whitespace-nowrap pb-1.5 px-1 border-b-2 font-semibold text-lg focus:outline-none transition-colors'
                                        ]"
                                    >
                                        {{ tab }}
                                    </button>
                                </Tab>
                            </template>
                        </TabList>
                    </div>

                    <div
                        v-if="programPackage.attendee_count"
                        class="inline-flex items-center text-gray-700 space-x-2 ml-4"
                    >
                        <UserGroupIcon
                            class="w-5 h-5"
                            aria-hidden="true"
                        />

                        <span class="font-bold text-xs/tight bg-gray-100 rounded-full px-2 py-1">
                            {{ programPackage.attendee_count }}
                        </span>
                    </div>
                </div>

                <TabPanels
                    as="div"
                    class="mt-6 lg:mt-10"
                >
                    <GeneralTabPanel
                        @show-all-contributors="changeTab(2)"
                        @show-all-cooperations="changeTab(3)"
                    />
                    <ProgramTabPanel />
                    <ContributorsTabPanel />
                    <CooperationsTabPanel />
                    <ReviewTabPanel :unmount="false" />
                </TabPanels>
            </TabGroup>
        </div>

        <CooperationList
            v-if="programPackage.display_on_overview_tab && selectedTab === 0"
            @show-more-info="changeTab(3)"
        />

        <div
            v-if="relatedContent && selectedTab === 0"
            class="container px-4 md:py-4 mx-auto"
        >
            <div class="grid grid-cols-1 lg:grid-cols-3 lg:gap-x-16">
                <RelatedContent />
            </div>
        </div>
    </div>
</template>
